import { Clock } from "lucide-react";
import { useCallback, useMemo, useState, type ReactNode } from "react";

import { timezonePlace } from "../events/timezones";
import {
  scheduleClockLabel,
  toDate,
  usableTimeZone,
  viewerClockHint,
} from "./agenda-schedule";
import {
  ScheduleItemCard,
  SessionLocationNotes,
  SessionPlace,
  SessionPresenters,
  YourTrackPill,
} from "./AgendaSchedule";
import {
  CHAPTER_NUMBER_CLASS,
  CHAPTER_NUMBER_SIZE,
  CHAPTER_STYLE,
  CHAPTER_TITLE_CLASS,
  CHAPTER_WHEN_CLASS,
  chapterTitleStyle,
} from "./ChapterBlocks";
import { timeSpan, type EventDates } from "./landing-model";
import {
  buildProgramme,
  highlightText,
  type ProgrammeBlock,
  type ProgrammeDay,
} from "./programme";
import type { AgendaItem, Track } from "./schemas";

/**
 * The Agenda tab for an event with highlights or day titles: per day a large
 * "Day N" header with the day's title, the highlighted sessions as colour
 * cards, and every other session as a card between them. Every session is
 * always listed; nothing folds away.
 *
 * The other sessions are the schedule's own cards, so a session shows the same
 * presenters, place, details and bookmark control as in the plain agenda, and
 * the clock line says which time zone the times are on.
 *
 * A highlighted session has no "Show details" fold. Its card already carries
 * the text, so a fold would open the same description again; what the card
 * does not say yet (location notes, a longer description) is written out on
 * it. The other sessions keep their fold, because nothing else shows their
 * description.
 */
export function ProgrammeSchedule({
  items,
  everything,
  tracks,
  timeZone,
  dayTitles,
  eventDates,
  idPrefix,
  renderActions,
  highlightTrackIds,
}: {
  /** What the reader's filters leave. */
  items: AgendaItem[];
  /** Every session with a time: days and chapters are numbered against it. */
  everything: AgendaItem[];
  tracks: Track[];
  timeZone?: string;
  dayTitles: Record<string, string> | null | undefined;
  /** The event's first and last date, which "Day N" counts from. */
  eventDates: EventDates;
  idPrefix: string;
  /**
   * The row's controls. The second argument is the title the card shows,
   * which a highlight may have written differently from the session's own.
   */
  renderActions: (item: AgendaItem, displayTitle?: string) => ReactNode;
  highlightTrackIds?: ReadonlySet<string>;
}) {
  const [expandedItems, setExpandedItems] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const zone = usableTimeZone(timeZone);
  const days = useMemo(
    () => buildProgramme(items, everything, zone, dayTitles, eventDates),
    [items, everything, zone, dayTitles, eventDates],
  );
  const { clock, hint, place } = useMemo(() => {
    const starts = items
      .map((item) => toDate(item.startsAt))
      .filter((date): date is Date => date !== null);
    const place = zone ? timezonePlace(zone) : "";
    return {
      clock: scheduleClockLabel(starts, zone),
      hint: zone ? viewerClockHint(starts, zone, place) : null,
      place,
    };
  }, [items, zone]);
  const trackById = useMemo(
    () => new Map(tracks.map((track) => [track.id, track])),
    [tracks],
  );
  const toggleExpanded = useCallback(
    (id: string) =>
      setExpandedItems((current) => {
        const next = new Set(current);
        if (!next.delete(id)) next.add(id);
        return next;
      }),
    [],
  );

  return (
    <div className="space-y-6" data-testid={`${idPrefix}-programme`}>
      {clock && (
        <div className="space-y-1 text-xs text-muted-foreground">
          <p
            className="flex items-center gap-1.5"
            data-testid={`${idPrefix}-clock`}
          >
            <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {zone
              ? `Times are in ${place} time (${clock}).`
              : `Times are in your time zone, ${clock}.`}
          </p>
          {hint && (
            <p className="pl-5" data-testid={`${idPrefix}-viewer-clock`}>
              {hint}
            </p>
          )}
        </div>
      )}

      {days.map((day) => (
        <DaySection
          key={day.key}
          day={day}
          zone={zone}
          idPrefix={idPrefix}
          trackById={trackById}
          expandedItems={expandedItems}
          onToggleExpanded={toggleExpanded}
          renderActions={renderActions}
          highlightTrackIds={highlightTrackIds}
        />
      ))}
    </div>
  );
}

function DaySection({
  day,
  zone,
  idPrefix,
  trackById,
  expandedItems,
  onToggleExpanded,
  renderActions,
  highlightTrackIds,
}: {
  day: ProgrammeDay;
  zone: string | undefined;
  idPrefix: string;
  trackById: Map<string, Track>;
  expandedItems: ReadonlySet<string>;
  onToggleExpanded: (id: string) => void;
  renderActions: (item: AgendaItem) => ReactNode;
  highlightTrackIds?: ReadonlySet<string>;
}) {
  const headingId = `${idPrefix}-day-heading-${day.key}`;
  return (
    <section
      id={`${idPrefix}-day-section-${day.key}`}
      aria-labelledby={headingId}
      className="space-y-3"
      data-testid={`${idPrefix}-programme-day-${day.key}`}
    >
      <div
        className="space-y-3 rounded-2xl border border-border bg-dusk-hero px-5 py-7 text-white dark:border-edge dark:border-lit-edge dark:shadow-lit"
        style={{ containerType: "inline-size" }}
      >
        <h2 id={headingId}>
          <span className="block text-sm uppercase tracking-[0.16em]">
            {day.heading}
          </span>
          <span
            className="mt-2 block font-display leading-[0.9] tracking-tight"
            style={{ fontSize: "clamp(4rem, 26cqi, 6.5rem)" }}
          >
            Day {day.number}
          </span>
        </h2>
        {day.title && (
          <p
            className="font-display text-3xl italic leading-tight text-hb-yellow wrap-anywhere"
            data-testid={`${idPrefix}-day-title-${day.key}`}
          >
            {day.title}
          </p>
        )}
      </div>

      {day.blocks.map((block) =>
        block.kind === "highlight" ? (
          <HighlightCard
            key={block.item.id}
            block={block}
            zone={zone}
            idPrefix={idPrefix}
            renderActions={renderActions}
            mine={
              highlightTrackIds !== undefined &&
              (block.item.trackIds ?? []).some((id) =>
                highlightTrackIds.has(id),
              )
            }
          />
        ) : (
          <SessionList
            key={block.id}
            block={block}
            zone={zone}
            idPrefix={idPrefix}
            trackById={trackById}
            expandedItems={expandedItems}
            onToggleExpanded={onToggleExpanded}
            renderActions={renderActions}
            highlightTrackIds={highlightTrackIds}
          />
        ),
      )}
    </section>
  );
}

function HighlightCard({
  block,
  zone,
  idPrefix,
  renderActions,
  mine,
}: {
  block: Extract<ProgrammeBlock, { kind: "highlight" }>;
  zone: string | undefined;
  idPrefix: string;
  renderActions: (item: AgendaItem, displayTitle?: string) => ReactNode;
  mine: boolean;
}) {
  const { item, chapter } = block;
  const { lead, more } = highlightText(item, chapter.blurb);
  return (
    <article
      data-testid={`${idPrefix}-item-${item.id}`}
      data-chapter={chapter.id}
      className={`flex flex-col gap-3 rounded-2xl px-5 pb-6 pt-5 ${CHAPTER_STYLE[chapter.slot].block}`}
      style={{ containerType: "inline-size" }}
    >
      <div className="flex items-start justify-between gap-3">
        <span
          aria-hidden="true"
          className={`${CHAPTER_NUMBER_CLASS} ${CHAPTER_NUMBER_SIZE.medium} ${CHAPTER_STYLE[chapter.slot].chip}`}
        >
          {chapter.number}
        </span>
        {renderActions(item, chapter.title)}
      </div>
      <h3
        className={CHAPTER_TITLE_CLASS}
        style={chapterTitleStyle(chapter.title)}
      >
        {chapter.title}
      </h3>
      <span
        className={CHAPTER_WHEN_CLASS}
        data-testid={`${idPrefix}-time-${item.id}`}
      >
        {timeSpan(item, zone)}
      </span>
      {mine && (
        <div className="flex">
          <YourTrackPill item={item} idPrefix={idPrefix} />
        </div>
      )}
      {lead && (
        <p
          className="text-base leading-normal whitespace-pre-line wrap-anywhere text-muted-foreground dark:text-inherit"
          data-testid={`${idPrefix}-blurb-${item.id}`}
        >
          {lead}
        </p>
      )}
      <SessionPlace item={item} idPrefix={idPrefix} />
      <SessionLocationNotes item={item} idPrefix={idPrefix} />
      <SessionPresenters item={item} />
      {more && (
        <p
          className="text-base leading-normal whitespace-pre-line wrap-anywhere text-muted-foreground dark:text-inherit"
          data-testid={`${idPrefix}-more-${item.id}`}
        >
          {more}
        </p>
      )}
    </article>
  );
}

function SessionList({
  block,
  zone,
  idPrefix,
  trackById,
  expandedItems,
  onToggleExpanded,
  renderActions,
  highlightTrackIds,
}: {
  block: Extract<ProgrammeBlock, { kind: "sessions" }>;
  zone: string | undefined;
  idPrefix: string;
  trackById: Map<string, Track>;
  expandedItems: ReadonlySet<string>;
  onToggleExpanded: (id: string) => void;
  renderActions: (item: AgendaItem) => ReactNode;
  highlightTrackIds?: ReadonlySet<string>;
}) {
  return (
    <ol className="space-y-3" data-testid={`${idPrefix}-sessions-${block.id}`}>
      {block.items.map((item) => (
        <li key={item.id}>
          <ScheduleItemCard
            item={item}
            timeZone={zone}
            idPrefix={idPrefix}
            trackById={trackById}
            expanded={expandedItems.has(item.id)}
            onToggleExpanded={onToggleExpanded}
            renderActions={renderActions}
            stackActionsOnPhone={false}
            highlighted={
              highlightTrackIds !== undefined &&
              (item.trackIds ?? []).some((id) => highlightTrackIds.has(id))
            }
          />
        </li>
      ))}
    </ol>
  );
}
